import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { FileText, Clock, CheckCircle } from 'lucide-react';
import CustomerQuoteRequestForm from '../components/CustomerQuoteRequestForm';
import PublicPageShell from '../components/layout/PublicPageShell';
import {
  HOME_BOOKING_EVENT_TYPES,
  readHomeBookingIntent,
  type HomeBookingEventType,
} from '../utils/homeBookingIntent';

function isEventType(value: string | null): value is HomeBookingEventType {
  return Boolean(value && (HOME_BOOKING_EVENT_TYPES as readonly string[]).includes(value));
}

const OffertPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const prefill = useMemo(() => {
    const fromQuery = {
      date: searchParams.get('date') || undefined,
      event: searchParams.get('event') || undefined,
      dateUndecided: searchParams.get('dateUndecided') === '1',
    };
    const fromSession = readHomeBookingIntent();
    const eventRaw = fromQuery.event || fromSession?.event || '';
    return {
      defaultEventDate: fromQuery.date || fromSession?.date || undefined,
      defaultDateUndecided: fromQuery.dateUndecided || fromSession?.dateUndecided || false,
      defaultEventType: isEventType(eventRaw) ? eventRaw : undefined,
    };
  }, [searchParams]);

  useEffect(() => {
    document.title = 'Berätta om ert event - festutrustning.se';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <Helmet>
        <title>Berätta om ert event - festutrustning.se</title>
        <meta
          name="description"
          content="Skicka några korta uppgifter om ert event så återkommer vi inom 24 timmar med förslag på upplägg och nästa steg."
        />
      </Helmet>

      <div className="pt-12 pb-8 bg-transparent border-b border-slate-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white leading-tight">
              Berätta om ert event
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
              Skicka några korta uppgifter så återkommer vi inom 24 timmar med förslag på upplägg och
              nästa steg.
            </p>
            <p className="mt-4 text-sm text-slate-500 max-w-2xl mx-auto">
              Du skickar en offertförfrågan — vi återkommer med förslag och pris. Inget köpkrav.
            </p>
          </div>
        </div>
      </div>

      <PublicPageShell>
        <section className="py-16 md:py-20 border-t border-slate-800">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-slate-900/35 border border-slate-800 p-6 md:p-8">
                  <h2 className="text-2xl font-bold mb-6 text-white">Så fungerar det</h2>
                  <ol className="space-y-5">
                    <li className="flex items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center bg-purple-600/20 text-purple-300 font-bold text-sm">
                        1
                      </span>
                      <div>
                        <p className="font-medium text-white">Fyll i formuläret</p>
                        <p className="text-sm text-slate-400 mt-1">
                          Berätta kort om eventet och vad ni behöver hjälp med.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center bg-purple-600/20 text-purple-300 font-bold text-sm">
                        2
                      </span>
                      <div>
                        <p className="font-medium text-white">Vi tar fram en offert</p>
                        <p className="text-sm text-slate-400 mt-1">
                          Vårt team går igenom dina önskemål och sätter ihop ett paket.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center bg-purple-600/20 text-purple-300 font-bold text-sm">
                        3
                      </span>
                      <div>
                        <p className="font-medium text-white">Du får offerten via e-post</p>
                        <p className="text-sm text-slate-400 mt-1">
                          Granska, acceptera eller ställ frågor – allt via en säker länk.
                        </p>
                      </div>
                    </li>
                  </ol>
                </div>

                <div className="bg-slate-900/35 border border-slate-800 p-6 md:p-8 space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="text-purple-400 shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-white">Svar inom 24 timmar</p>
                      <p className="text-sm text-slate-400">Vi återkommer så snart vi kan på vardagar.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="text-purple-400 shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-white">Ingen förpliktelse</p>
                      <p className="text-sm text-slate-400">Offerten är kostnadsfri och helt utan bindning.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-purple-400 shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-white">Skräddarsytt paket</p>
                      <p className="text-sm text-slate-400">Vi anpassar utrustningen efter ditt event och din budget.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <CustomerQuoteRequestForm
                  variant="inline"
                  packageType="Offertförfrågan"
                  onClose={() => {}}
                  defaultEventDate={prefill.defaultEventDate}
                  defaultDateUndecided={prefill.defaultDateUndecided}
                  defaultEventType={prefill.defaultEventType}
                />
              </div>
            </div>
          </div>
        </section>
      </PublicPageShell>
    </>
  );
};

export default OffertPage;
